/**
 * AllInventoryAddStepper
 * Unified bulk add dialog — lets users add stock across ALL 6 inventory
 * types in a single session.
 *
 * Step 0 — Select Items & Location
 * Step 1 — Configure Stock Details
 * Step 2 — Upload Documents (optional)
 * Step 3 — Review & Submit
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog, Box, Typography, IconButton, Stepper, Step, StepLabel,
  StepContent, Button, CircularProgress, Chip, useTheme, alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Inventory as InventoryIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useInventoryItems } from '../../../shared/useInventoryData';
import { getInventoryConfig } from '../../../shared/inventoryConfig';
import { submitAddStock } from '../../../Add/AddInventoryStepper/stepperHelpers';
import { submitAddStockChemical } from '../../../Add/AddInventoryStepper/chemicalStepperHelpers';
import { submitAddStockProduct } from '../../../Add/addConfigs/addProductConfig';
import BulkAddItemSelect from './BulkAddItemSelect';
import BulkAddItemConfig, { initLot } from './BulkAddItemConfig';
import StepUploadDocuments from '../../../Add/AddInventoryStepper/StepUploadDocuments';

// ── Constants ─────────────────────────────────────────────────────────────────

const TYPE_COLORS = {
  products:  '#1565c0',
  chemicals: '#7b1fa2',
  caps:      '#00796b',
  bottles:   '#2e7d32',
  labels:    '#e65100',
  supplies:  '#5d4037',
};

const STEPS = [
  'Select Items & Location',
  'Configure Stock Details',
  'Upload Documents',
  'Review & Submit'
];


// ── Review helper components ───────────────────────────────────────────────────

function ReviewRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 90, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
}

function ProductLotReview({ lot }) {
  const enablePallets  = lot._enablePallets ?? false;
  const enableCases    = lot._enableCases   ?? true;
  const pQty           = parseInt(lot.palletQty) || 0;
  const cQty           = parseInt(lot.caseQty)   || 0;
  const uQty           = parseInt(lot.unitQty)   || 0;
  const unitsPerCase   = parseInt(lot._unitsPerCase)   || 1;
  const casesPerPallet = parseInt(lot._casesPerPallet) || 1;
  const totalUnits     = (pQty * casesPerPallet * unitsPerCase) + (cQty * unitsPerCase) + uQty;

  const qtyParts = [];
  if (enablePallets && pQty > 0) qtyParts.push(`${pQty} Pallet${pQty !== 1 ? 's' : ''}`);
  if (enableCases   && cQty > 0) qtyParts.push(`${cQty} Case${cQty !== 1 ? 's' : ''}`);
  if (uQty > 0)                   qtyParts.push(`${uQty} Unit${uQty !== 1 ? 's' : ''}`);

  const qtyStr = qtyParts.length
    ? `${qtyParts.join(' + ')}${totalUnits > 0 ? ` = ${totalUnits.toLocaleString()} units total` : ''}`
    : '—';

  return (
    <Box>
      {lot._configName && <ReviewRow label="Unit Config" value={lot._configName} />}
      <ReviewRow label="Quantity" value={qtyStr} />
      <ReviewRow label="Lot / Batch" value={`${lot.lotNumber} / ${lot.batchNumber}`} />
      {lot.mfgDate    && <ReviewRow label="Mfg Date"    value={lot.mfgDate} />}
      {lot.expiryDate && <ReviewRow label="Expiry Date" value={lot.expiryDate} />}
    </Box>
  );
}

function ChemicalLotReview({ lot, item }) {
  const unitType    = item.primary_unit_type     || 'units';
  const unitMeasure = item.primary_unit_measure  || '';
  const unitQtyPer  = parseFloat(item.primary_unit_quantity) || null;
  const qty         = parseFloat(lot.quantity) || 0;
  const lotMode     = lot.quantityMode || 'container';
  let qtyStr;
  if (lotMode === 'measure' && unitQtyPer) {
    qtyStr = `${qty} ${unitMeasure} = ${(qty / unitQtyPer).toFixed(3)} ${unitType}`;
  } else {
    const calcTotal = unitQtyPer && qty > 0 ? (qty * unitQtyPer).toFixed(3) : null;
    qtyStr = `${qty} ${unitType}${calcTotal ? ` = ${calcTotal}${unitMeasure}` : ''}`;
  }

  return (
    <Box>
      <ReviewRow label="Quantity"   value={qtyStr} />
      <ReviewRow label="Lot / Batch" value={`${lot.lotNumber} / ${lot.batchNumber}`} />
      <ReviewRow label="Expiry Date" value={lot.expiryDate || '—'} />
      <ReviewRow label="Unit Cost"  value={`$${parseFloat(lot.unitCost || 0).toFixed(2)} per ${unitType}`} />
    </Box>
  );
}

function GenericLotReview({ lot }) {
  return (
    <Box>
      <ReviewRow label="Quantity"   value={`${parseFloat(lot.quantity || 0).toLocaleString()} units`} />
      <ReviewRow label="Lot / Batch" value={`${lot.lotNumber} / ${lot.batchNumber}`} />
      <ReviewRow label="Unit Cost"  value={`$${parseFloat(lot.unitCost || 0).toFixed(2)}`} />
    </Box>
  );
}


// ── Main component ─────────────────────────────────────────────────────────────

export default function AllInventoryAddStepper({
  open, onBack, onClose, companyId, locations, suppliers, onSuccess
}) {
  const theme = useTheme();

  // ── Fetch all 6 types unconditionally (React hook rules) ───────────────────
  const { items: productItems,  loading: l1 } = useInventoryItems('products',  companyId);
  const { items: chemicalItems, loading: l2 } = useInventoryItems('chemicals', companyId);
  const { items: capsItems,     loading: l3 } = useInventoryItems('caps',      companyId);
  const { items: bottlesItems,  loading: l4 } = useInventoryItems('bottles',   companyId);
  const { items: labelsItems,   loading: l5 } = useInventoryItems('labels',    companyId);
  const { items: suppliesItems, loading: l6 } = useInventoryItems('supplies',  companyId);

  const allItems = useMemo(() => ({
    products:  productItems,
    chemicals: chemicalItems,
    caps:      capsItems,
    bottles:   bottlesItems,
    labels:    labelsItems,
    supplies:  suppliesItems,
  }), [productItems, chemicalItems, capsItems, bottlesItems, labelsItems, suppliesItems]);

  const itemsLoading = l1 || l2 || l3 || l4 || l5 || l6;

  // ── State ──────────────────────────────────────────────────────────────────
  const [activeStep,       setActiveStep]       = useState(0);
  const [selectedItems,    setSelectedItems]    = useState([]);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [itemsConfig,      setItemsConfig]      = useState({});
  const [shared, setShared] = useState({
    vendor: null, invoiceNumber: '', invoiceDate: '', poNumber: '', poDate: ''
  });
  const [documents, setDocuments] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setActiveStep(0);
      setSelectedItems([]);
      setSelectedLocation(null);
      setItemsConfig({});
      setShared({ vendor: null, invoiceNumber: '', invoiceDate: '', poNumber: '', poDate: '' });
      setDocuments({});
      setSubmitting(false);
    }
  }, [open]);

  // ── Step 0 → 1: init per-item configs ─────────────────────────────────────
  const handleGoToConfigure = () => {
    const newConfig = { ...itemsConfig };
    for (const item of selectedItems) {
      const key = `${item.inventoryType}:${item.id}`;
      if (!newConfig[key]) {
        if (item.inventoryType === 'products') {
          newConfig[key] = { variants: [] };
        } else {
          newConfig[key] = { lots: [initLot(item.inventoryType, item)] };
        }
      }
    }
    setItemsConfig(newConfig);
    setActiveStep(1);
  };

  // ── Validation ─────────────────────────────────────────────────────────────
  const canProceedStep0 = selectedItems.length > 0 && selectedLocation !== null;

  const canProceedStep1 = useMemo(() => {
    if (selectedItems.length === 0) return false;
    return selectedItems.every(item => {
      const key = `${item.inventoryType}:${item.id}`;
      const cfg = itemsConfig[key];
      if (item.inventoryType === 'products') {
        if (!cfg?.variants?.length) return false;
        return cfg.variants.every(variant =>
          variant.lots?.length > 0 && variant.lots.every(lot => {
            const hasQty    = (parseInt(lot.palletQty) || 0) > 0 || (parseInt(lot.caseQty) || 0) > 0 || (parseInt(lot.unitQty) || 0) > 0;
            const hasConfig = !!lot._configId;
            return hasQty && lot.lotNumber && lot.batchNumber && hasConfig;
          })
        );
      }
      if (!cfg?.lots?.length) return false;
      return cfg.lots.every(lot => {
        const hasQty    = parseFloat(lot.quantity) > 0;
        const hasCost   = lot.unitCost !== '' && parseFloat(lot.unitCost) >= 0;
        return hasQty && lot.lotNumber && lot.batchNumber && hasCost;
      });
    });
  }, [selectedItems, itemsConfig]);

  // ── Total lot count (for review) ──────────────────────────────────────────
  const totalLots = useMemo(() =>
    Object.values(itemsConfig).reduce((sum, cfg) => {
      if (cfg?.variants) {
        return sum + cfg.variants.reduce((s, v) => s + (v.lots?.length || 0), 0);
      }
      return sum + (cfg?.lots?.length || 0);
    }, 0),
  [itemsConfig]);

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSubmitting(true);
    const results = [];
    try {
      for (const item of selectedItems) {
        const key    = `${item.inventoryType}:${item.id}`;
        const cfg    = itemsConfig[key];
        const config = getInventoryConfig(item.inventoryType);
        const commonData = {
          item,
          location:      selectedLocation,
          vendor:        shared.vendor,
          invoiceNumber: shared.invoiceNumber || null,
          invoiceDate:   shared.invoiceDate   || null,
          poNumber:      shared.poNumber      || null,
          poDate:        shared.poDate        || null,
          documents,
          notes:     null
        };

        // Products: iterate per-variant, each with own lots
        if (item.inventoryType === 'products') {
          for (const variant of (cfg?.variants || [])) {
            for (const lot of (variant.lots || [])) {
              const result = await submitAddStockProduct(companyId, 'products', config, {
                ...commonData,
                lotNumber:    lot.lotNumber,
                batchNumber:  lot.batchNumber,
                palletQty:    lot.palletQty  || '0',
                caseQty:      lot.caseQty    || '0',
                unitQty:      lot.unitQty    || '0',
                mfgDate:      lot.mfgDate    || null,
                expiryDate:   lot.expiryDate || null,
                shelfLife:    null,
                variantId:    variant.id     || null,
                unitConfigId: lot._configId  || null,
                unitCost:     null
              });
              results.push({ ...result, itemName: item.name, type: item.inventoryType });
            }
          }
          continue;
        }

        for (const lot of (cfg?.lots || [])) {
          let result;

          if (item.inventoryType === 'chemicals') {
            result = await submitAddStockChemical(companyId, 'chemicals', config, {
              ...commonData,
              lotNumber:    lot.lotNumber,
              batchNumber:  lot.batchNumber,
              quantity:     parseFloat(lot.quantity),
              quantityMode: lot.quantityMode || 'container',
              unitCost:     lot.unitCost,
              expiryDate:   lot.expiryDate || null
            });
          } else {
            result = await submitAddStock(companyId, item.inventoryType, config, {
              ...commonData,
              lotNumber:   lot.lotNumber,
              batchNumber: lot.batchNumber,
              quantity:    parseFloat(lot.quantity),
              unitCost:    lot.unitCost
            });
          }

          results.push({ ...result, itemName: item.name, type: item.inventoryType });
        }
      }

      const successCount = results.filter(r => r.success).length;
      const failCount    = results.length - successCount;

      if (failCount === 0) {
        onSuccess?.(
          `Successfully added stock for ${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''} (${successCount} lot${successCount !== 1 ? 's' : ''})`
        );
        onClose();
      } else {
        onSuccess?.(`${successCount} succeeded, ${failCount} failed`, 'warning');
      }
    } catch (err) {
      console.error('Bulk add submit error:', err);
      onSuccess?.(`Error: ${err.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      disableEscapeKeyDown
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          border: 'none',
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.95)}, ${alpha(theme.palette.grey[100], 0.95)})`,
          boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          overflow: 'hidden',
          maxHeight: '92vh'
        }
      }}
    >
      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            p: 1.5, borderRadius: '50%',
            bgcolor: alpha(theme.palette.success.main, 0.15),
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <AddIcon sx={{ fontSize: 28, color: 'success.main' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>Bulk Add Stock</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Add stock across any inventory types in one session
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {selectedItems.length > 0 && (
            <Chip
              icon={<InventoryIcon sx={{ fontSize: 14 }} />}
              label={`${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''} selected`}
              size="small"
              color="primary"
              variant="outlined"
            />
          )}
          {onBack && (
            <IconButton
              onClick={onBack}
              sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }, transition: 'all 0.2s ease' }}
            >
              <ArrowBackIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}
          <IconButton
            onClick={onClose}
            sx={{
              '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', transform: 'rotate(90deg)' },
              transition: 'all 0.2s ease'
            }}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Stepper body */}
      <Box sx={{ p: 0, maxHeight: 'calc(92vh - 120px)', overflowY: 'auto' }}>
        <Box sx={{ p: 3 }}>
          <Stepper activeStep={activeStep} orientation="vertical">

            {/* ── Step 0: Select ─────────────────────────────────────────── */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {STEPS[0]}
                </Typography>
              </StepLabel>
              <StepContent>
                <BulkAddItemSelect
                  allItems={allItems}
                  loading={itemsLoading}
                  selectedItems={selectedItems}
                  onSelectedItemsChange={setSelectedItems}
                  selectedLocation={selectedLocation}
                  onLocationChange={setSelectedLocation}
                  locations={locations || []}
                />
                <Box sx={{ mt: 3 }}>
                  <Button
                    variant="contained"
                    onClick={handleGoToConfigure}
                    disabled={!canProceedStep0}
                    sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500, fontSize: '1rem' }}
                  >
                    Continue
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* ── Step 1: Configure ──────────────────────────────────────── */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {STEPS[1]}
                </Typography>
              </StepLabel>
              <StepContent>
                <BulkAddItemConfig
                  companyId={companyId}
                  selectedItems={selectedItems}
                  itemsConfig={itemsConfig}
                  onItemsConfigChange={setItemsConfig}
                  shared={shared}
                  onSharedChange={setShared}
                  suppliers={suppliers || []}
                />
                <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                  <Button
                    onClick={() => setActiveStep(0)}
                    sx={{ borderRadius: 2, px: 4, py: 1.5, fontSize: '1rem' }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep(2)}
                    disabled={!canProceedStep1}
                    sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500, fontSize: '1rem' }}
                  >
                    Continue
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* ── Step 2: Upload Documents ────────────────────────────────── */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {STEPS[2]}
                </Typography>
              </StepLabel>
              <StepContent>
                <StepUploadDocuments
                  adjustmentType="add"
                  documents={documents}
                  onDocumentsChange={setDocuments}
                  config={{ hasUnitConfig: selectedItems.every(i => i.inventoryType === 'products'), hasAnyProduct: selectedItems.some(i => i.inventoryType === 'products') }}
                />
                <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
                  <Button
                    onClick={() => setActiveStep(1)}
                    sx={{ borderRadius: 2, px: 4, py: 1.5, fontSize: '1rem' }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={() => setActiveStep(3)}
                    sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500, fontSize: '1rem' }}
                  >
                    Review
                  </Button>
                </Box>
              </StepContent>
            </Step>

            {/* ── Step 3: Review & Submit ────────────────────────────────── */}
            <Step>
              <StepLabel>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  {STEPS[3]}
                </Typography>
              </StepLabel>
              <StepContent>
                {/* Header summary */}
                <Box sx={{
                  mb: 2.5, p: 2, borderRadius: 2,
                  bgcolor: alpha(theme.palette.success.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    Adding <strong>{totalLots}</strong> lot{totalLots !== 1 ? 's' : ''} across{' '}
                    <strong>{selectedItems.length}</strong> item{selectedItems.length !== 1 ? 's' : ''} to{' '}
                    <strong>{selectedLocation?.name}</strong>
                  </Typography>
                  {shared.vendor && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Vendor: {shared.vendor.name}
                      {shared.invoiceNumber ? ` · Invoice #${shared.invoiceNumber}` : ''}
                      {shared.invoiceDate ? ` · ${shared.invoiceDate}` : ''}
                    </Typography>
                  )}
                  {Object.keys(documents).length > 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      Documents: {Object.keys(documents).join(', ')}
                    </Typography>
                  )}
                </Box>

                {/* Per-item review cards */}
                {selectedItems.map(item => {
                  const key      = `${item.inventoryType}:${item.id}`;
                  const cfg      = itemsConfig[key] || { lots: [], variants: [] };
                  const lots     = cfg.lots || [];
                  const variants = cfg.variants || [];
                  const color    = TYPE_COLORS[item.inventoryType] || '#666';
                  const isProduct = item.inventoryType === 'products';
                  const totalLotCount = isProduct
                    ? variants.reduce((s, v) => s + (v.lots?.length || 0), 0)
                    : lots.length;

                  return (
                    <Box key={key} sx={{
                      mb: 2, borderRadius: 2,
                      border: `1px solid ${alpha(color, 0.25)}`,
                      overflow: 'hidden'
                    }}>
                      {/* Item header */}
                      <Box sx={{
                        px: 2.5, py: 1.5,
                        bgcolor: alpha(color, 0.07),
                        borderBottom: `1px solid ${alpha(color, 0.2)}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 500, color }}>
                          {item.name}
                        </Typography>
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 13, color: `${color} !important` }} />}
                          label={isProduct
                            ? `${variants.length} variant${variants.length !== 1 ? 's' : ''} · ${totalLotCount} lot${totalLotCount !== 1 ? 's' : ''}`
                            : `${totalLotCount} lot${totalLotCount !== 1 ? 's' : ''}`}
                          size="small"
                          sx={{
                            bgcolor: alpha(color, 0.12), color, fontWeight: 500,
                            border: `1px solid ${alpha(color, 0.2)}`
                          }}
                        />
                      </Box>

                      {/* Products: per-variant lot rows */}
                      {isProduct && variants.map((variant, vi) => (
                        <Box key={variant.id}>
                          <Box sx={{
                            px: 2.5, py: 1,
                            bgcolor: alpha(color, 0.04),
                            borderBottom: `1px solid ${alpha(color, 0.12)}`
                          }}>
                            <Typography variant="caption" sx={{
                              fontWeight: 500, color,
                              fontSize: '0.72rem'
                            }}>
                              {variant.name}
                            </Typography>
                          </Box>
                          {(variant.lots || []).map((lot, li) => (
                            <Box key={li} sx={{
                              px: 2.5, py: 1.5,
                              borderBottom: (vi < variants.length - 1 || li < variant.lots.length - 1)
                                ? `1px solid ${alpha(theme.palette.grey[200], 0.8)}`
                                : 'none',
                              bgcolor: li % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.6)
                            }}>
                              {variant.lots.length > 1 && (
                                <Typography variant="caption" sx={{
                                  fontWeight: 500, color: 'text.secondary',
                                  fontSize: '0.7rem', display: 'block', mb: 0.75,
                                  textTransform: 'uppercase', letterSpacing: '0.5px'
                                }}>
                                  Lot #{li + 1}
                                </Typography>
                              )}
                              <ProductLotReview lot={lot} />
                            </Box>
                          ))}
                        </Box>
                      ))}

                      {/* Non-products: flat lot rows */}
                      {!isProduct && lots.map((lot, li) => (
                        <Box key={li} sx={{
                          px: 2.5, py: 1.5,
                          borderBottom: li < lots.length - 1
                            ? `1px solid ${alpha(theme.palette.grey[200], 0.8)}`
                            : 'none',
                          bgcolor: li % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.6)
                        }}>
                          {lots.length > 1 && (
                            <Typography variant="caption" sx={{
                              fontWeight: 500, color: 'text.secondary',
                              fontSize: '0.7rem', display: 'block', mb: 0.75,
                              textTransform: 'uppercase', letterSpacing: '0.5px'
                            }}>
                              Lot #{li + 1}
                            </Typography>
                          )}
                          {item.inventoryType === 'chemicals' && <ChemicalLotReview lot={lot} item={item} />}
                          {!['products', 'chemicals'].includes(item.inventoryType) && <GenericLotReview lot={lot} />}
                        </Box>
                      ))}
                    </Box>
                  );
                })}

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    onClick={() => setActiveStep(2)}
                    sx={{ borderRadius: 2, px: 4, py: 1.5, fontSize: '1rem' }}
                  >
                    Back
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{
                      borderRadius: 2, px: 5, py: 1.5,
                      fontWeight: 500, fontSize: '1rem',
                      bgcolor: 'success.main',
                      '&:hover': { bgcolor: 'success.dark' }
                    }}
                  >
                    {submitting ? 'Submitting...' : 'Submit All'}
                  </Button>
                </Box>
              </StepContent>
            </Step>

          </Stepper>
        </Box>
      </Box>
    </Dialog>
  );
}
